import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { Lock, RefreshCw, Search, Download, LogOut, BarChart3, Users, Calendar, Check, AlertCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "capexara_admin_token";
const LEADS_POLL_MS = 15_000;
const STATS_POLL_MS = 30_000;

function formatApiError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

function csvEscape(v) {
  const s = v == null ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function formatRelative(date, now) {
  if (!date) return "syncing…";
  const diff = Math.max(0, Math.floor((now - date.getTime()) / 1000));
  if (diff < 5) return "just now";
  if (diff < 60) return `${diff}s ago`;
  const m = Math.floor(diff / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ago`;
}

function downloadCsv(rows, filename) {
  const headers = ["created_at", "source", "name", "company", "email", "industry", "platforms", "message", "id"];
  const lines = [headers.join(",")];
  rows.forEach((l) => {
    lines.push([
      l.created_at,
      l.source || "form",
      l.name,
      l.company,
      l.email,
      l.project_type || "",
      (l.platforms || []).join(" | "),
      l.message || "",
      l.id,
    ].map(csvEscape).join(","));
  });
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function AdminLeads() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [leads, setLeads] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState("leads"); // "leads" | "insights"
  const [consentStats, setConsentStats] = useState(null);
  const [sourceFilter, setSourceFilter] = useState("all"); // "all" | "form" | "calendly"
  const [lastSync, setLastSync] = useState(null);    // Date of last successful poll
  const [tickNow, setTickNow] = useState(Date.now()); // re-renders the "x s ago" label
  const [newCount, setNewCount] = useState(0);        // leads added since last user interaction
  const knownIdsRef = useRef(new Set());
  const hasSyncedOnceRef = useRef(false);

  /**
   * Fetch leads. `silent=true` suppresses the loading spinner and used by the
   * 15s background poller — keeps the UI from flickering on every refresh.
   */
  const loadLeads = async (t, { silent = false } = {}) => {
    if (!silent) setLoading(true);
    setErr("");
    try {
      const { data } = await axios.get(`${API}/admin/leads`, {
        headers: { "X-Admin-Token": t },
      });
      // Diff against the last known set to surface a "+N new" indicator.
      // Skip the diff on the very first sync — every lead is "old" then.
      if (hasSyncedOnceRef.current) {
        const fresh = data.filter((l) => !knownIdsRef.current.has(l.id));
        if (fresh.length > 0) setNewCount((n) => n + fresh.length);
      }
      knownIdsRef.current = new Set(data.map((l) => l.id));
      hasSyncedOnceRef.current = true;
      setLeads(data);
      setLastSync(new Date());
    } catch (e) {
      if (e?.response?.status === 401) {
        setErr("Session expired. Please sign in again.");
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      } else if (!silent) {
        setErr(formatApiError(e?.response?.data?.detail) || "Unable to load leads.");
      }
      if (!silent) setLeads(null);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const loadConsentStats = async (t) => {
    try {
      const { data } = await axios.get(`${API}/admin/consent-stats`, {
        headers: { "X-Admin-Token": t },
      });
      setConsentStats(data);
    } catch {
      setConsentStats({ total: 0, accept_all: 0, reject_non_essential: 0, custom: 0, analytics_opt_in: 0, marketing_opt_in: 0, timeline: [] });
    }
  };

  useEffect(() => {
    if (token) {
      loadLeads(token);
      loadConsentStats(token);
    }
    // eslint-disable-next-line
  }, []);

  /**
   * Real-time polling — refreshes leads every 15s and consent stats every 30s
   * while the tab is visible and the admin is signed in. Stops cleanly on
   * logout, tab switch (to Insights vs Leads governs which endpoint is hit),
   * tab-hidden (Page Visibility API) and unmount.
   */
  useEffect(() => {
    if (!token) return undefined;
    let leadsTimer = null;
    let statsTimer = null;

    const tickLeads = () => {
      if (document.hidden) return;
      loadLeads(token, { silent: true });
    };
    const tickStats = () => {
      if (document.hidden) return;
      loadConsentStats(token);
    };

    if (tab === "leads") {
      leadsTimer = setInterval(tickLeads, LEADS_POLL_MS);
    } else {
      statsTimer = setInterval(tickStats, STATS_POLL_MS);
    }

    // When the tab becomes visible again, do one immediate refresh so the
    // operator sees the freshest state without waiting for the next interval.
    const onVis = () => {
      if (!document.hidden) {
        if (tab === "leads") loadLeads(token, { silent: true });
        else loadConsentStats(token);
      }
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      if (leadsTimer) clearInterval(leadsTimer);
      if (statsTimer) clearInterval(statsTimer);
      document.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line
  }, [token, tab]);

  /** Tick the "x s ago" relative-time label every second. */
  useEffect(() => {
    if (!token || !lastSync) return undefined;
    const id = setInterval(() => setTickNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [token, lastSync]);

  const submitLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, {
        email: email.trim(),
        password,
      });
      const t = data.token;
      localStorage.setItem(TOKEN_KEY, t);
      setToken(t);
      setPassword("");
      await loadLeads(t);
      await loadConsentStats(t);
    } catch (e2) {
      setErr(formatApiError(e2?.response?.data?.detail) || "Login failed.");
      setLoading(false);
    }
  };

  const logout = () => {
    setToken("");
    setLeads(null);
    setEmail("");
    setPassword("");
    setLastSync(null);
    setNewCount(0);
    knownIdsRef.current = new Set();
    hasSyncedOnceRef.current = false;
    localStorage.removeItem(TOKEN_KEY);
  };

  // Filtered list
  const filtered = useMemo(() => {
    if (!leads) return [];
    let list = leads;
    if (sourceFilter !== "all") {
      list = list.filter((l) => (l.source || "form") === sourceFilter);
    }
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((l) => {
      return (
        (l.name || "").toLowerCase().includes(q) ||
        (l.company || "").toLowerCase().includes(q) ||
        (l.email || "").toLowerCase().includes(q) ||
        (l.project_type || "").toLowerCase().includes(q) ||
        (l.message || "").toLowerCase().includes(q)
      );
    });
  }, [leads, query, sourceFilter]);

  const sourceCounts = useMemo(() => {
    if (!leads) return { all: 0, form: 0, calendly: 0 };
    const c = { all: leads.length, form: 0, calendly: 0 };
    leads.forEach((l) => {
      const s = l.source === "calendly" ? "calendly" : "form";
      c[s] += 1;
    });
    return c;
  }, [leads]);

  // ────── LOGIN GATE ──────
  if (!token || !leads) {
    return (
      <section className="admin-shell">
        <div className="container">
          <div className="admin-gate" data-testid="admin-gate">
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, color: "var(--steel)" }}>
              <Lock size={20} />
              <span className="mono" style={{ fontSize: 11, letterSpacing: ".2em", textTransform: "uppercase" }}>Restricted</span>
            </div>
            <h1>Capexara AI · Admin</h1>
            <p>Sign in to view and triage demo requests.</p>
            <form onSubmit={submitLogin}>
              <label className="admin-label mono" htmlFor="admin-email">Email</label>
              <input
                id="admin-email"
                className="admin-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="hello@capexara.com"
                autoComplete="username"
                autoFocus
                required
                data-testid="admin-email-input"
              />
              <label className="admin-label mono" htmlFor="admin-password" style={{ marginTop: 14 }}>Password</label>
              <input
                id="admin-password"
                className="admin-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••"
                autoComplete="current-password"
                required
                data-testid="admin-password-input"
              />
              {err && <div className="admin-error" data-testid="admin-error">{err}</div>}
              <button type="submit" className="btn btn-primary" disabled={loading} data-testid="admin-login-btn" style={{ width: "100%", justifyContent: "center", marginTop: 18 }}>
                {loading ? "Signing in…" : "Sign in"} <span className="arrow">→</span>
              </button>
            </form>
          </div>
        </div>
      </section>
    );
  }

  // ────── DASHBOARD ──────
  const stats = {
    total: leads.length,
    today: leads.filter((l) => new Date(l.created_at).toDateString() === new Date().toDateString()).length,
    thisWeek: leads.filter((l) => new Date(l.created_at).getTime() >= Date.now() - 7 * 864e5).length,
    companies: new Set(leads.map((l) => (l.company || "").toLowerCase())).size,
  };

  const exportFilename = `capexara-leads-${new Date().toISOString().slice(0, 10)}.csv`;

  return (
    <section className="admin-shell">
      <div className="container admin-dash" data-testid="admin-dashboard">
        <div className="admin-head">
          <div>
            <div className="mono">◉ Capexara AI · Admin</div>
            <h1>{tab === "leads" ? "Leads" : "Consent Insights"}</h1>
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <div className={"admin-live-pill" + (document.hidden ? " is-paused" : "")} data-testid="admin-live-pill" title={document.hidden ? "Paused — tab is hidden" : "Auto-refreshing"}>
              <span className="admin-live-dot" aria-hidden="true" />
              <span className="admin-live-label">{document.hidden ? "Paused" : "Live"}</span>
              <span className="admin-live-meta">· updated {formatRelative(lastSync, tickNow)}</span>
              {newCount > 0 && (
                <span className="admin-live-new" data-testid="admin-live-new">
                  +{newCount} new
                </span>
              )}
            </div>
            {tab === "leads" && (
              <>
                <button className="btn btn-ghost" onClick={() => { setNewCount(0); loadLeads(token); }} data-testid="admin-refresh-btn">
                  <RefreshCw size={14} style={{ marginRight: 6 }} /> Refresh
                </button>
                <button className="btn btn-ghost" onClick={() => downloadCsv(filtered, exportFilename)} data-testid="admin-export-csv-btn" disabled={!filtered.length}>
                  <Download size={14} style={{ marginRight: 6 }} /> Export CSV ({filtered.length})
                </button>
              </>
            )}
            {tab === "insights" && (
              <button className="btn btn-ghost" onClick={() => loadConsentStats(token)} data-testid="admin-insights-refresh-btn">
                <RefreshCw size={14} style={{ marginRight: 6 }} /> Refresh
              </button>
            )}
            <button className="btn btn-ghost" onClick={logout} data-testid="admin-logout-btn">
              <LogOut size={14} style={{ marginRight: 6 }} /> Sign out
            </button>
          </div>
        </div>

        <div className="admin-tabs" role="tablist">
          <button type="button" className={"admin-tab " + (tab === "leads" ? "is-active" : "")} role="tab" aria-selected={tab === "leads"} onClick={() => setTab("leads")} data-testid="admin-tab-leads">
            <Users size={14} /> Leads
          </button>
          <button type="button" className={"admin-tab " + (tab === "insights" ? "is-active" : "")} role="tab" aria-selected={tab === "insights"} onClick={() => setTab("insights")} data-testid="admin-tab-insights">
            <BarChart3 size={14} /> Insights
          </button>
        </div>

        {tab === "insights" && <InsightsPanel stats={consentStats} />}

        {tab === "leads" && (
          <>
            <div className="admin-stat-row">
              <div className="admin-stat" data-testid="admin-stat-total">
                <div className="admin-stat-label">Total leads</div>
                <div className="admin-stat-value">{stats.total}</div>
              </div>
              <div className="admin-stat">
                <div className="admin-stat-label">Today</div>
                <div className="admin-stat-value">{stats.today}</div>
              </div>
              <div className="admin-stat">
                <div className="admin-stat-label">Last 7 days</div>
                <div className="admin-stat-value">{stats.thisWeek}</div>
              </div>
              <div className="admin-stat">
                <div className="admin-stat-label">Unique companies</div>
                <div className="admin-stat-value">{stats.companies}</div>
              </div>
            </div>

            <div className="admin-search-row">
              <div className="admin-source-filter" data-testid="admin-source-filter">
                {[
                  { key: "all", label: "All", count: sourceCounts.all },
                  { key: "form", label: "Form", count: sourceCounts.form },
                  { key: "calendly", label: "Calendly", count: sourceCounts.calendly },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    className={"admin-source-pill " + (sourceFilter === opt.key ? `is-active is-${opt.key}` : "")}
                    onClick={() => setSourceFilter(opt.key)}
                    data-testid={`admin-source-pill-${opt.key}`}
                  >
                    {opt.label} <span className="admin-source-count">{opt.count}</span>
                  </button>
                ))}
              </div>
              <div className="admin-search">
                <Search size={16} className="admin-search-icon" />
                <input
                  type="text"
                  className="admin-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by company, name, email, industry, or message…"
                  data-testid="admin-search-input"
                />
                {query && (
                  <button type="button" className="admin-search-clear" onClick={() => setQuery("")} aria-label="Clear search">×</button>
                )}
              </div>
              <div className="admin-search-meta mono">
                Showing <strong>{filtered.length}</strong> of {leads.length}
              </div>
            </div>

            <div className="admin-table-wrap">
              <table className="admin-table" data-testid="admin-leads-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Source</th>
                    <th>Name</th>
                    <th>Company</th>
                    <th>Email</th>
                    <th>Industry</th>
                    <th>Message</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr><td colSpan="7" style={{ textAlign: "center", color: "var(--text-3)", padding: 40 }}>
                      {leads.length === 0 ? "No leads yet." : "No leads match your search."}
                    </td></tr>
                  )}
                  {filtered.map((l) => {
                    const src = l.source === "calendly" ? "calendly" : "form";
                    return (
                      <tr key={l.id} data-testid={`admin-lead-row-${l.id}`}>
                        <td className="mono" style={{ whiteSpace: "nowrap" }}>{new Date(l.created_at).toLocaleString()}</td>
                        <td><span className={`source-pill source-pill--${src}`} data-testid={`source-pill-${src}`}>{src === "calendly" ? "Calendly" : "Form"}</span></td>
                        <td>{l.name}</td>
                        <td>{l.company}</td>
                        <td className="mono"><a href={`mailto:${l.email}`}>{l.email}</a></td>
                        <td>{l.project_type || "—"}</td>
                        <td style={{ maxWidth: 340, whiteSpace: "normal" }}>{l.message || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function InsightsPanel({ stats }) {
  if (!stats) return <div className="admin-insights-empty">Loading consent insights…</div>;
  if (stats.total === 0) {
    return (
      <div className="admin-insights-empty" data-testid="admin-insights-empty">
        No consent decisions logged yet. As visitors interact with the cookie banner, their (anonymous) decisions will appear here.
      </div>
    );
  }
  const pct = (n) => stats.total ? Math.round((n / stats.total) * 100) : 0;
  const max = Math.max(1, ...stats.timeline.map((d) => d.count));
  return (
    <div data-testid="admin-insights-panel">
      <div className="admin-stat-row">
        <div className="admin-stat" data-testid="insight-stat-total">
          <div className="admin-stat-label">Total decisions</div>
          <div className="admin-stat-value">{stats.total}</div>
        </div>
        <div className="admin-stat">
          <div className="admin-stat-label">Accept all</div>
          <div className="admin-stat-value">{pct(stats.accept_all)}%</div>
          <div className="admin-stat-sub">{stats.accept_all} visitors</div>
        </div>
        <div className="admin-stat">
          <div className="admin-stat-label">Reject non-essential</div>
          <div className="admin-stat-value">{pct(stats.reject_non_essential)}%</div>
          <div className="admin-stat-sub">{stats.reject_non_essential} visitors</div>
        </div>
        <div className="admin-stat">
          <div className="admin-stat-label">Customized</div>
          <div className="admin-stat-value">{pct(stats.custom)}%</div>
          <div className="admin-stat-sub">{stats.custom} visitors</div>
        </div>
      </div>

      <div className="admin-stat-row" style={{ marginTop: 16 }}>
        <div className="admin-stat">
          <div className="admin-stat-label">Analytics opt-in</div>
          <div className="admin-stat-value">{pct(stats.analytics_opt_in)}%</div>
          <div className="admin-stat-sub">{stats.analytics_opt_in} of {stats.total}</div>
        </div>
        <div className="admin-stat">
          <div className="admin-stat-label">Marketing opt-in</div>
          <div className="admin-stat-value">{pct(stats.marketing_opt_in)}%</div>
          <div className="admin-stat-sub">{stats.marketing_opt_in} of {stats.total}</div>
        </div>
      </div>

      <div className="admin-insights-block">
        <div className="admin-insights-title mono">Daily decisions (last 30 days)</div>
        <div className="admin-insights-bars" data-testid="insight-timeline">
          {stats.timeline.map((d) => (
            <div key={d.day} className="admin-insights-bar-col" title={`${d.day}: ${d.count}`}>
              <div className="admin-insights-bar" style={{ height: `${(d.count / max) * 100}%` }} />
              <div className="admin-insights-bar-label">{d.day.slice(5)}</div>
            </div>
          ))}
          {stats.timeline.length === 0 && <div className="admin-insights-empty" style={{ flex: 1 }}>No daily data yet.</div>}
        </div>
      </div>

      <div className="admin-insights-quote">
        <strong>Privacy quote (paste into security questionnaires):</strong>
        <em>
          "Capexara AI honors visitor consent on every page load. {pct(stats.reject_non_essential)}% of our visitors reject non-essential
          cookies — and we collect zero analytics from them. By design."
        </em>
      </div>

      <CalendlySetupCard />
    </div>
  );
}

function CalendlySetupCard() {
  const [pat, setPat] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(""); setResult(null);
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const { data } = await axios.post(`${API}/admin/calendly-setup`, {
        personal_access_token: pat.trim(),
        base_url: window.location.origin.replace(/\/$/, ""),
      }, { headers: { "X-Admin-Token": token } });
      setResult(data);
      setPat("");
    } catch (e2) {
      setErr(formatApiError(e2?.response?.data?.detail) || "Setup failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-cal-card" data-testid="admin-calendly-setup">
      <div className="admin-cal-head">
        <div className="admin-cal-icon"><Calendar size={18} /></div>
        <div>
          <div className="admin-insights-title mono" style={{ marginBottom: 4 }}>◉ Calendly Webhook</div>
          <div className="admin-cal-title">One-click webhook setup</div>
        </div>
      </div>
      <p className="admin-cal-desc">
        Paste your Calendly Personal Access Token and we'll register a webhook so every booking lands as a lead in this dashboard.
        Your token is used once for setup — never stored on our servers.
        <br />
        <a href="https://calendly.com/integrations/api_webhooks" target="_blank" rel="noopener noreferrer">Get a token →</a>
      </p>
      <form onSubmit={submit}>
        <input
          type="password"
          className="admin-input"
          placeholder="eyJraWQ…  (Calendly Personal Access Token)"
          value={pat}
          onChange={(e) => setPat(e.target.value)}
          required
          autoComplete="off"
          data-testid="cal-pat-input"
        />
        <button type="submit" className="btn btn-primary" disabled={busy || !pat.trim()} data-testid="cal-setup-btn" style={{ marginTop: 12 }}>
          {busy ? "Connecting…" : "Connect Calendly"} <span className="arrow">→</span>
        </button>
      </form>
      {err && (
        <div className="admin-cal-msg admin-cal-msg-err" data-testid="cal-setup-err">
          <AlertCircle size={14} /> {err}
        </div>
      )}
      {result && result.ok && (
        <div className="admin-cal-msg admin-cal-msg-ok" data-testid="cal-setup-success">
          <Check size={14} />
          <div>
            <div><strong>Connected as {result.user?.name || result.user?.email}.</strong></div>
            <div className="mono" style={{ fontSize: 11, marginTop: 4, color: "var(--text-3)", wordBreak: "break-all" }}>
              Webhook → {result.callback_url}
            </div>
            {result.removed_existing?.length > 0 && (
              <div className="mono" style={{ fontSize: 11, color: "var(--text-3)", marginTop: 4 }}>
                Replaced {result.removed_existing.length} previous webhook(s) pointing here.
              </div>
            )}
            <div style={{ fontSize: 12, marginTop: 8, color: "var(--text-2)" }}>
              Try it: book a slot via your Calendly link — within seconds it'll appear in the Leads tab.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
